import * as Sentry from '@sentry/react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import React from 'react'
import ReactDOM from 'react-dom/client'

import EmployerRenewalsApp from 'apps/employer_renewals/pages/EmployerRenewalsApp'
import { InvalidOrExpiredLinkPage } from 'apps/pages/InvalidOrExpiredLinkPage'
import { NotFoundPage } from 'apps/pages/NotFoundPage'
import WorkflowTasksApp from 'components/admin/workflow_tasks/WorkflowTasksApp'
import { OwnerIdentificationBanner } from 'components/OwnerIdentificationBanner'
import { EmployerOnboarding } from 'pages/employer_onboarding'
import { OwnerInvitationForm } from 'pages/employer_owner_invitation_form'

import AdminInvitePartnerAdmin from './admin/AdminInvitePartnerAdmin'
import EmployerInformation from './admin/EmployerBasicInformation'
import EmployerOpenEnrollment from './admin/EmployerOpenEnrollment'
import EmployerReviewsView from './admin/EmployerReviewsView'
import EmployerReviewView from './admin/EmployerReviewView'
import GranularAllowanceConfigs from './admin/granular_allowance_configs/GranularAllowanceConfigs'
import IneligibleSpendReports from './admin/IneligibleSpendReports'
import AdminLedgerAccountListTable from './admin/LedgerAccountList'
import AdminLedgerEntryList from './admin/LedgerEntryList'
import AdminLedgerExplorer from './admin/LedgerExplorer'
import MedicalPlanDataTable from './admin/medical_plan_data/MedicalPlanDataTable'
import MedicalPlanIngestionTable from './admin/medical_plan_ingestions/MedicalPlanIngestionTable'
import MemberConsistentDeductions from './admin/MemberConsistentDeductions'
import { MergeIsolvedClientSelector } from './admin/MergeIsolvedClientSelector'
import PartnerRelationshipSelect from './admin/PartnerRelationshipSelect'
import { AdminPayrollProviderConfigList } from './admin/PayrollProviderConfigList'
import MappedPlansTable from './admin/plan_descendant_mappings/MappedPlansTable'
import PlanDescendantMappings from './admin/plan_descendant_mappings/PlanDescendantMappings'
import UnmappedPlansTable from './admin/plan_descendant_mappings/UnmappedPlansTable'
import ReimbursementRequestsView from './admin/ReimbursementRequestsView'
import StripePriceSelect from './admin/StripePriceSelect'
import RejectedSubstantiationsTable from './admin/substantiations/RejectedSubstantiationsTable'
import UnmatchedReceiptsTable from './admin/substantiations/UnmatchedReceiptsTable'
import SubstantiationsView from './admin/SubstantiationsView'
import BrokerEmployeesTable from './brokers/BrokersEmployeesTable'
import BrokersEmployersTable from './brokers/BrokersEmployersTable'
import BrokersEnrollment from './brokers/BrokersEnrollment'
import BrokersMembersTransactionsTable from './brokers/BrokersMembersTransactionsTable'
import BrokersMembersTransactionsView from './brokers/BrokersMembersTransactionsView'
import BrokersMembersView from './brokers/BrokersMembersView'
import CoverageAuditLogsMembersView from './brokers/coverage_audit_logs/CoverageAuditLogsMembersView'
import CoverageAuditLogsView from './brokers/coverage_audit_logs/CoverageAuditLogsView'
import CoveredParticipantsView from './brokers/covered_participants/CoveredParticipantsView'
import EnrollmentPaymentStatusPill from './brokers/EnrollmentPaymentStatusPill'
import NeedsAttentionMemberView from './brokers/needs_attention/NeedsAttentionMemberView'
import NeedsAttentionView from './brokers/needs_attention/NeedsAttentionView'
import QualifyingLifeEventsView from './brokers/qualifying_life_events/QualifyingLifeEventsView'
import StripeCardsMembersView from './brokers/stripe_cards/StripeCardsMembersView'
import TasksMembersView from './brokers/tasks/TasksMembersView'
import TasksView from './brokers/tasks/TasksView'
import Transactions from './brokers/Transactions'
import BrokersUnprocessedEnrollmentsTable from './brokers/unprocessed_enrollments/BrokersUnprocessedEnrollmentsTable'
import WorkflowsView from './brokers/workflows/WorkflowsView'
import ConfirmAllowancesDialog from './ConfirmAllowancesDialog'
import AppConfigContext, { type AppConfigContextType } from './context/AppConfigContext'
import DeductionsPage from './deductions/DeductionsPage'
import EmployeesDuplicatesTable from './EmployeesDuplicatesTable'
import EmployeesTable from './EmployeesTable'
import ADPEmbeddedPage from './employer_onboarding/ADPEmbeddedPage'
import ADPEmbeddedResetPage from './employer_onboarding/ADPEmbeddedResetPage'
import EmployerInvoice from './EmployerInvoice'
import { EmployerOnboardingPayrollConnectionPicker } from './EmployerOnboardingPayrollConnectionPicker'
import EmployersResourcesPage from './employers/EmployersResourcesPage'
import EmployerSwitcher from './employers/EmployerSwitcher'
import RenewalsDashboardBanner from './employers/RenewalsDashboardBanner'
import EnrollmentEvents from './EnrollmentEvents'
import FinchApiExplorer from './FinchApiExplorer'
import ICHRADashboardHeader from './ICHRADashboardHeader'
import ICHRADepositAccountDetails from './ICHRADepositAccountDetails'
import { Marketplace, MarketplaceCheckout, MarketplaceProduct, MarketplacePurchaseConfirmation } from './marketplace'
import MerchantAdminOnboarding from './marketplace/admin/Onboarding'
import MedicalPlanProjections from './MedicalPlanProjections'
import EnrollmentEventErrors from './member_onboarding_v2/EnrollmentEventErrors'
import OnboardingOrchestrator from './member_onboarding_v2/OnboardingOrchestrator'
import GetStartedPage from './member_onboarding_v2/pages/GetStartedPage'
import PostEnrollmentPage from './member_onboarding_v2/pages/PostEnrollmentPage'
import MemberIchraConfig from './MemberIchraConfig'
import MemberOnboardingBanner from './MemberOnboardingBanner'
import MemberOnboardingSupportPopover from './MemberOnboardingSupportPopover'
import MemberRenewalsBanner from './MemberRenewalsBanner'
import SidebarNavItem from './members/components/SidebarNavItem/SidebarNavItem'
import MemberHomePage from './members/pages/MemberHomePage'
import MemberTransactionsPage from './members/pages/MemberTransactionsPage'
import MemberTransactions from './MemberTransactions'
import { AdminOneOffReimbursementsForm } from './one_off_reimbursement_form/AdminOneOffReimbursementsForm'
import MemberOneOffReimbursementsForm from './one_off_reimbursement_form/MemberOneOffReimbursementsForm'
import AgencyDetailsForm from './partners/AgencyDetailsForm'
import PartnersEmployeesTable from './partners/PartnersEmployeesTable'
import PartnersEmployersTable from './partners/PartnersEmployersTable'
import PartnersPayoutsTable from './partners/PartnersPayoutsTable'
import PartnersQuotesList from './partners/PartnersQuotesList'
import PartnersResourcesPage from './partners/PartnersResourcesPage'
import PartnersTeamPage from './partners/PartnersTeamPage'
import ReferralPageDetailsForm from './partners/ReferralPageDetailsForm'
import ReferralPageViewsTable from './partners/ReferralPageViewsTable'
import RequestAQuoteButton from './partners/RequestAQuoteButton'
import RequestAQuotePanel from './partners/RequestAQuotePanel'
import PartnerSettings from './partners/Settings'
import SwitchToEmployerButton from './partners/SwitchToEmployerButton'
import ManualDeductionsOnboarding from './payroll/ManualDeductionsOnboarding'
import { MissingScheduleForAutomationHeaderBanner } from './payroll/MissingScheduleForAutomationHeaderBanner'
import PaySchedules from './payroll/PaySchedules'
import { UnassignedEmployeesHeaderBanner } from './payroll/UnassignedEmployeesHeaderBanner'
import { UnassignedEmployeesTable } from './payroll/UnassignedEmployeesTable'
import { UnverifiedSchedulesHeaderBanner } from './payroll/UnverifiedSchedulesHeaderBanner'
import PayrollFilesInstructions from './PayrollFilesInstructions'
import PayrollSettings from './PayrollSettings'
import PayrollSyncStatusHeader from './PayrollSyncStatusHeader'
import PayScheduleStatus from './PayScheduleStatus'
import ProspectCensusDetails from './prospects/ProspectCensusDetails'
import ProspectDetails from './prospects/ProspectDetails'
import ProspectsList from './prospects/ProspectsList'
import QuoteDetails from './prospects/QuoteDetails'
import ProviderSearch from './ProviderSearch'
import QualifyingLifeEventView from './qualifying_life_event/QualifyingLifeEventView'
import QuoteExplorer from './quote_explorer/QuoteExplorer'
import QuoteProcessing from './quote_explorer/QuoteProcessing'
import MedicalPlanAvailabilityNotice from './quoting/employees/MedicalPlanAvailabilityNotice'
import CreateProspect from './quoting/prospects/CreateProspect'
import EditProspect from './quoting/prospects/EditProspect'
import QuoteFlowSideNav from './quoting/QuoteFlowSideNav'
import { QuoteFlowTopNav } from './quoting/QuoteFlowTopNav'
import BenchmarkSuggestions from './quoting/quotes/benchmark_suggestions/BenchmarkSuggestions'
import CreateOrUpdateQuote from './quoting/quotes/CreateOrUpdateQuote'
import CurrentBenefits from './quoting/quotes/CurrentBenefits'
import EditQuote from './quoting/quotes/EditQuote'
import FlatAllowanceExplorer from './quoting/quotes/flat_allowances/FlatAllowanceExplorer'
import SelectQuoteStrategy from './quoting/quotes/SelectQuoteStrategy'
import AdminEmployerNotes from './reusable/AdminEmployerNotes'
import Badge from './reusable/Badge'
import { Banner } from './reusable/Banner'
import ImageUpload from './reusable/ImageUpload'
import IntercomLauncher from './reusable/intercom/IntercomLauncher'
import MemberNotes from './reusable/MemberNotes'
import { MetalTierBadge } from './reusable/MetalTierBadge'
import { TabAnchorMenu, TabMenu } from './reusable/TabMenu'
import { Toast } from './reusable/Toast'
import SelectCoverages from './SelectCoverages'
import ThatchSearch from './ThatchSearch'
import VerifyResourceAccessToken from './VerifyResourceAccessToken'
import FeedbackPromptContainer from '../employers/FeedbackPromptContainer'
import EmployerAllowancesSummary from './admin/EmployerAllowancesSummary'
import { BrokersMembersListView } from './brokers/members/list'

type Components = Record<string, React.ElementType>

const components: Components = {
  ADPEmbeddedPage,
  ADPEmbeddedResetPage,
  AdminInvitePartnerAdmin,
  AdminPayrollProviderConfigList,
  AdminLedgerAccountListTable,
  AdminLedgerEntryList,
  AdminLedgerExplorer,
  EmployerOpenEnrollment,
  GranularAllowanceConfigs,
  MedicalPlanDataTable,
  MedicalPlanIngestionTable,
  IneligibleSpendReports,
  MemberConsistentDeductions,
  IntercomLauncher,
  MemberOnboardingSupportPopover,
  MemberOneOffReimbursementsForm,
  EmployeesTable,
  RenewalsDashboardBanner,
  EmployeesDuplicatesTable,
  EmployerOnboardingPayrollConnectionPicker,
  EmployerSwitcher,
  PayrollFilesInstructions,
  PayrollSettings,
  PayScheduleStatus,
  ProviderSearch,
  ConfirmAllowancesDialog,
  DeductionsPage,
  ManualDeductionsOnboarding,
  BrokersEmployersTable,
  BrokersEnrollment,
  BrokersUnprocessedEnrollmentsTable,
  BrokerEmployeesTable,
  // TODO: Remove once NAV Transaction View v2 migration is complete.
  BrokersMembersTransactionsTable,
  ICHRADashboardHeader,
  ICHRADepositAccountDetails,
  PartnersEmployersTable,
  MemberTransactions,
  NeedsAttentionMemberView,
  NeedsAttentionView,
  EnrollmentEvents,
  EnrollmentPaymentStatusPill,
  EmployersResourcesPage,
  FeedbackPromptContainer,
  Transactions,
  AgencyDetailsForm,
  ReferralPageDetailsForm,
  ReferralPageViewsTable,
  PartnerSettings,
  PartnersEmployeesTable,
  PartnersPayoutsTable,
  SelectCoverages,
  ImageUpload,
  EmployerInvoice,
  ProspectsList,
  ProspectDetails,
  ProspectCensusDetails,
  QuoteDetails,
  PartnersQuotesList,
  RequestAQuoteButton,
  RequestAQuotePanel,
  SwitchToEmployerButton,
  PartnersResourcesPage,
  PartnersTeamPage,
  FinchApiExplorer,
  MergeIsolvedClientSelector,
  AdminEmployerNotes,
  MemberRenewalsBanner,
  MemberOnboardingBanner,
  SidebarNavItem,
  MemberIchraConfig,
  MemberHomePage,
  MemberTransactionsPage,
  AdminOneOffReimbursementsForm,
  ThatchSearch,
  EnrollmentEventErrors,
  OnboardingOrchestrator,
  GetStartedPage,
  PostEnrollmentPage,
  BrokersMembersTransactionsView,
  Marketplace,
  MarketplaceProduct,
  MarketplaceCheckout,
  MarketplacePurchaseConfirmation,
  MerchantAdminOnboarding,
  QualifyingLifeEventView,
  QualifyingLifeEventsView,
  ReimbursementRequestsView,
  TasksView,
  WorkflowTasksApp,
  MemberNotes,
  TasksMembersView,
  SubstantiationsView,
  WorkflowsView,
  EmployerOnboarding,
  EmployerRenewalsApp,
  OwnerInvitationForm,
  NotFoundPage,
  BrokersMembersListView,
  BrokersMembersView,
  StripeCardsMembersView,
  // Lookbook components
  Badge,
  MetalTierBadge,
  Banner,
  TabMenu,
  TabAnchorMenu,
  PaySchedules,
  QuoteExplorer,
  StripePriceSelect,
  CreateProspect,
  CreateOrUpdateQuote,
  EditQuote,
  VerifyResourceAccessToken,
  QuoteFlowSideNav,
  CurrentBenefits,
  SelectQuoteStrategy,
  EditProspect,
  QuoteProcessing,
  UnmatchedReceiptsTable,
  UnassignedEmployeesHeaderBanner,
  UnverifiedSchedulesHeaderBanner,
  MissingScheduleForAutomationHeaderBanner,
  UnassignedEmployeesTable,
  MedicalPlanProjections,
  PlanDescendantMappings,
  BenchmarkSuggestions,
  CoveredParticipantsView,
  FlatAllowanceExplorer,
  QuoteFlowTopNav,
  PayrollSyncStatusHeader,
  RejectedSubstantiationsTable,
  CoverageAuditLogsView,
  CoverageAuditLogsMembersView,
  MedicalPlanAvailabilityNotice,
  EmployerReviewView,
  EmployerInformation,
  EmployerReviewsView,
  InvalidOrExpiredLinkPage,
  OwnerIdentificationBanner,
  UnmappedPlansTable,
  MappedPlansTable,
  PartnerRelationshipSelect,
  EmployerAllowancesSummary,
}

const queryClient = new QueryClient()

/**
 * This function looks for all elements with a `data-react-component` attribute
 * and mounts the corresponding React component to that element.
 *
 * It is called when the DOM is first loaded and when Turbo renders a page when
 * visiting with Turbo Drive enabled (since there is no DOMContentLoaded event
 * in that case, as the DOM is already loaded and Turbo just replaces the body).
 *
 * To use your React components in a Rails ERB template, you can use the
 * `react_component` helper, e.g.:
 * react_component("Example", { prop1: "value1", prop2: "value2" })
 */
function mount () {
  const mountPoints = document.querySelectorAll('[data-react-component]')
  mountPoints.forEach((mountPoint) => {
    const { dataset } = mountPoint as HTMLElement
    const componentName = dataset.reactComponent
    if (componentName) {
      const Component = components[componentName]
      if (Component) {
        const props = JSON.parse(dataset.props as string)
        const root = ReactDOM.createRoot(mountPoint)
        const environment = dataset.environment as AppConfigContextType['environment']

        root.render(
          <AppConfigContext.Provider value={{ environment }}>
            <Sentry.ErrorBoundary>
              <QueryClientProvider client={queryClient}>
                <Component {...props} />
                <Toast />
              </QueryClientProvider>
            </Sentry.ErrorBoundary>
          </AppConfigContext.Provider>
        )
      } else {
        console.warn(
          'WARNING: No component found for: ',
          dataset.reactComponent,
          components
        )
      }
    }
  })
}

export default function initReact (): void {
  document.addEventListener('DOMContentLoaded', mount)
  document.addEventListener('turbo:render', mount)
}
